import React, { useEffect, useRef, useState } from 'react';

/**
 * One subtle tooltip per visit inviting music play.
 * Never repeats after dismiss or music start.
 */
export const MUSIC_ATTENTION_CONFIG = {
  messages: ['🎵 Play a tune?', '🎶 Music?', '✨ Hit play!'],
  initialDelayMs: 1800,
  visibleMs: 60000,
  fadeOutMs: 400,
  sessionPlayedKey: 'wesam-music-played',
  sessionTooltipKey: 'wesam-music-tooltip-shown',
};

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

export default function MusicAttentionBubble({
  musicPlaying,
  onPlayMusic,
  config = MUSIC_ATTENTION_CONFIG,
}) {
  const [phase, setPhase] = useState('idle'); // idle | show | hide
  const [message, setMessage] = useState('');
  const timers = useRef([]);
  const stopped = useRef(false);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const schedule = (fn, ms) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
    return id;
  };

  const stopAttention = () => {
    stopped.current = true;
    clearTimers();
    setPhase('idle');
  };

  const markShown = () => {
    try {
      sessionStorage.setItem(config.sessionTooltipKey, '1');
    } catch (_) {
      /* ignore */
    }
  };

  useEffect(() => {
    try {
      if (sessionStorage.getItem(config.sessionPlayedKey) === '1') {
        stopped.current = true;
        return;
      }
      if (sessionStorage.getItem(config.sessionTooltipKey) === '1') {
        stopped.current = true;
      }
    } catch (_) {
      /* private mode */
    }
  }, [config.sessionPlayedKey, config.sessionTooltipKey]);

  useEffect(() => {
    if (stopped.current || musicPlaying) return undefined;

    schedule(() => {
      if (stopped.current || musicPlaying) return;
      setMessage(pick(config.messages));
      setPhase('show');
      markShown();

      schedule(() => {
        if (stopped.current) return;
        setPhase('hide');
        schedule(() => {
          if (stopped.current) return;
          setPhase('idle');
          stopped.current = true;
        }, config.fadeOutMs);
      }, config.visibleMs);
    }, config.initialDelayMs);

    return clearTimers;
    // Mount-once for session timing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!musicPlaying) return;
    try {
      sessionStorage.setItem(config.sessionPlayedKey, '1');
    } catch (_) {
      /* ignore */
    }
    stopAttention();
  }, [musicPlaying, config.sessionPlayedKey]);

  const bubbleVisible = phase === 'show' || phase === 'hide';

  if (musicPlaying || !bubbleVisible) {
    return null;
  }

  return (
    <div
      className={`music-attention ${
        phase === 'hide' ? 'music-attention--out' : 'music-attention--in'
      }`}
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        className="music-attention__bubble"
        onClick={(e) => {
          e.stopPropagation();
          if (typeof onPlayMusic === 'function') onPlayMusic();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label={`${message} Play music`}
      >
        {message}
      </button>
    </div>
  );
}
